/**
 * Chamadas HTTP dos documentos pessoais.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/documentos` que a tela usa —
 * listar, criar, alterar e excluir um documento.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou.
 */
import { type Documento, type NovoDocumento, documentoSchema } from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeDocumentosSchema = z.array(documentoSchema);

/**
 * Lê todos os documentos, por pessoa e, dentro dela, na ordem da grade.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os documentos cadastrados, cada um com a pessoa aninhada.
 */
export function buscarDocumentos(sinal?: AbortSignal): Promise<Documento[]> {
  return clienteApi.get('/documentos', { schema: listaDeDocumentosSchema, sinal });
}

/**
 * Cria um documento.
 *
 * A API responde 409 em dois casos que o formulário precisa distinguir na
 * mensagem: a pessoa já tem um documento daquele tipo, ou a pessoa escolhida
 * deixou de existir enquanto o formulário estava aberto.
 *
 * @param dados - O documento preenchido, já validado por `novoDocumentoSchema`.
 * @returns O documento como ficou gravado, com a pessoa aninhada.
 */
export function criarDocumento(dados: NovoDocumento): Promise<Documento> {
  return clienteApi.post('/documentos', dados, { schema: documentoSchema });
}

/**
 * Substitui todos os campos de um documento.
 *
 * Trocar o tipo ou a pessoa esbarra na mesma restrição da criação, e responde
 * 409 sem alterar nada.
 *
 * @param id - Identificador da linha em `documentos_pessoais`.
 * @param dados - O documento inteiro, e não apenas os campos alterados.
 * @returns O documento como ficou gravado.
 */
export function atualizarDocumento(id: string, dados: NovoDocumento): Promise<Documento> {
  return clienteApi.put(`/documentos/${encodeURIComponent(id)}`, dados, {
    schema: documentoSchema,
  });
}

/**
 * Apaga um documento.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `documentos_pessoais`.
 */
export function excluirDocumento(id: string): Promise<void> {
  return clienteApi.remover(`/documentos/${encodeURIComponent(id)}`);
}
